import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { Divider } from "@src/appV2/redesign/system/ui-components";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { TimesheetTimeEntryRow } from "./TimesheetTimeEntryRow";
import { TimesheetTotalsFooter } from "./TimesheetTotalsFooter";

interface TimesheetTimeEntriesProps {
  /** Hide pencil icons when editing should be disabled (e.g., timekeeping integrations) */
  hidePencilIcons: boolean;
  disabled?: boolean;
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  onEditClockIn: () => void;
  onEditBreak: () => void;
  onEditClockOut: () => void;
  formatBreakDisplay: () => string;
  /** Pass the original time when clock-in has been adjusted from the recorded value */
  originalClockInTime?: Date;
  /** Pass the original time when clock-out has been adjusted from the recorded value */
  originalClockOutTime?: Date;
  /** Pass the original formatted break string when break has been adjusted */
  originalBreakDisplay?: string;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown below the Total row when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
  /** When true, shows "Verified hours" label instead of "Total". Gate behind isMissedPunchRequestEnabledV2. */
  shouldShowVerifiedHoursLabel?: boolean;
}

export function TimesheetTimeEntries(props: TimesheetTimeEntriesProps) {
  const {
    hidePencilIcons,
    disabled = false,
    displayTimesheet,
    workplaceTimezone,
    onEditClockIn,
    onEditBreak,
    onEditClockOut,
    formatBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    originalBreakDisplay,
    verifiedTimesheet,
    shouldShowVerifiedHoursLabel = false,
  } = props;

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock in"
        originalValue={
          isDefined(originalClockInTime) ? formatWorkplaceTime(originalClockInTime) : undefined
        }
      >
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockIn}
        >
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Button>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow iconType="break" label="Break" originalValue={originalBreakDisplay}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditBreak}
        >
          {formatBreakDisplay()}
        </Button>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock out"
        originalValue={
          isDefined(originalClockOutTime) ? formatWorkplaceTime(originalClockOutTime) : undefined
        }
      >
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockOut}
        >
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Button>
      </TimesheetTimeEntryRow>

      <Divider />

      <TimesheetTotalsFooter
        displayTimesheet={displayTimesheet}
        verifiedTimesheet={verifiedTimesheet}
        shouldShowVerifiedHoursLabel={shouldShowVerifiedHoursLabel}
      />
    </Stack>
  );
}
